import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField
} from '@mui/material';
import { api } from '../../../services/api';
import sectionAvatarService from '../../../services/sectionAvatarService';
import sectionColorService from '../../../services/sectionColorService';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import CustomAvatarSelector from './CustomAvatarSelector';
import PermissionGate from '../../common/PermissionGate';

/**
 * Section Orchestrator Component for Foundation Page
 * Shows an orchestrator label, avatar image, and name for each section
 * Using the same design as in SectionedTaskSelector
 * 
 * @param {Object} props - Component props
 * @param {string} props.sectionId - ID of the section
 * @param {string} props.sectionName - Name of the section
 * @param {string} props.projectId - ID of the current project
 * @param {function} props.onAvatarChange - Optional callback when avatar changes
 * @param {Object} props.consolidatedOrchestrator - Pre-fetched orchestrator data from consolidated API
 * @param {boolean} props.useConsolidatedData - Whether to use consolidated data instead of individual API calls
 * @returns {React.ReactElement} SectionOrchestrator component
 */
const SectionOrchestrator = ({
  sectionId,
  sectionName,
  projectId,
  avatarUrl,
  avatarName,
  onAvatarChange,
  consolidatedOrchestrator,
  useConsolidatedData
}) => {
  const [loading, setLoading] = useState(!avatarUrl);
  const [error, setError] = useState(null);
  // No default avatar names - always fetch from API
  const getDefaultAvatarName = () => {
    // No default names, always use what comes from the API
    return '';
  };

  const [avatarData, setAvatarData] = useState({
    url: avatarUrl || null,
    name: avatarName || null,
    sectionId: sectionId
  });

  // Avatar selection dialog state
  const [avatarDialogOpen, setAvatarDialogOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  // Don't initialize with a default value - only use what comes from API
  const [customAvatarName, setCustomAvatarName] = useState('');
  const [savingAvatar, setSavingAvatar] = useState(false);
  const [nameError, setNameError] = useState('');
  // Flag to prevent useEffect from overwriting user-saved data
  const [userJustSaved, setUserJustSaved] = useState(false);


  useEffect(() => {
    let cancelled = false;

    // PRIORITY 1: If avatarUrl or avatarName props are provided (from parent state after save), use them
    // This ensures user-saved data takes precedence over stale consolidated data
    if (avatarUrl || avatarName) {
      setAvatarData({
        url: avatarUrl,
        name: avatarName || '',
        sectionId: sectionId
      });
      setLoading(false);
      return;
    }

    // PRIORITY 2: If using consolidated data and it has actual values, use that
    if (useConsolidatedData && consolidatedOrchestrator) {
      // Only use consolidated data if it has actual avatar values
      if (consolidatedOrchestrator.avatar_url || consolidatedOrchestrator.name) {
        setAvatarData({
          url: consolidatedOrchestrator.avatar_url,
          name: consolidatedOrchestrator.name,
          sectionId: sectionId
        });
        setLoading(false);
        return;
      }
      // Consolidated data exists but has no avatar - set empty state
      setAvatarData({
        url: null,
        name: null,
        sectionId: sectionId
      });
      setLoading(false);
      return;
    }

    // If projectId is not available, we can't fetch project-specific orchestrators
    if (!projectId) {
      console.log('No projectId provided, cannot fetch project-specific orchestrator');
      setLoading(false);
      return;
    }

    // Only fetch from API if not using consolidated data

    // Show current cache state for debugging
    sectionAvatarService.listAllAvatarStorage();

    // Don't check for newly created projects in session storage
    // Always fetch from the API for all projects

    // Fetch avatar data from service
    const fetchSectionAvatar = async () => {
      try {
        setLoading(true);

        if (!sectionId) {
          const defaultData = {
            url: null,
            name: null, // Use null to ensure we use default names in the UI
            sectionId: sectionId
          };
          setAvatarData(defaultData);
          setLoading(false);
          return;
        }

        // Use the avatar service to get project-specific avatar
        const data = await sectionAvatarService.getSectionAvatar(sectionId, projectId);

        if (cancelled) return;

        if (data.success && data.avatarUrl) {
          // Successfully got avatar data with actual URL
          const newAvatarData = {
            url: data.avatarUrl,
            name: data.avatarName || '',
            sectionId: sectionId
          };

          // Update local state
          setAvatarData(newAvatarData);

          // Call callback if provided and data changed
          if (onAvatarChange && (newAvatarData.url !== avatarData.url || newAvatarData.name !== avatarData.name)) {
            onAvatarChange(newAvatarData.url, newAvatarData.name);
          }
        } else {
          // No valid avatar with URL returned, use empty state
          const defaultData = {
            url: null,
            name: null, // Use null to ensure we use default names in the UI
            sectionId: sectionId
          };
          setAvatarData(defaultData);
        }

        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching section avatar:', err);
        setError('Failed to load orchestrator data');

        // Set default when fetch fails
        const defaultData = {
          url: null,
          name: null, // Use null to ensure we use default names in the UI
          sectionId: sectionId
        };
        setAvatarData(defaultData);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    // Execute the fetch
    fetchSectionAvatar();

    return () => { cancelled = true; };
    // Updated dependencies to include consolidated data
  }, [sectionId, sectionName, projectId, avatarUrl, avatarName, onAvatarChange, useConsolidatedData, consolidatedOrchestrator]);

  // Handle image loading error
  const handleImageError = () => {
    setAvatarData(prev => ({
      ...prev,
      url: null
    }));
  };

  // Update customAvatarName whenever avatarData changes
  // This effect only runs when avatarData changes from an external source
  useEffect(() => {
    if (avatarData.name) {
      setCustomAvatarName(avatarData.name);
    } else {
      // If no name from API, keep it empty
      setCustomAvatarName('');
    }
    // We intentionally don't include customAvatarName in the dependencies
    // to avoid circular updates
  }, [avatarData.name]);

  // Handle opening avatar dialog
  const handleOpenAvatarDialog = () => {
    setSelectedAvatar(null);
    // Only use avatarData.name from API, don't use section name as fallback
    if (avatarData.name) {
      console.log(`Using existing name for dialog: ${avatarData.name}`);
      setCustomAvatarName(avatarData.name);
    } else {
      // Keep empty if no name from API
      setCustomAvatarName('');
    }
    setNameError('');
    setAvatarDialogOpen(true);
  };

  // Handle closing avatar dialog
  const handleCloseAvatarDialog = () => {
    setAvatarDialogOpen(false);
  };

  // Handle avatar selection in dialog
  const handleSelectAvatar = (avatar) => {
    if (!avatar) return;
    setSelectedAvatar(avatar);
    // Always update the name when a new avatar is selected
    if (avatar.name) {
      setCustomAvatarName(avatar.name);
    }
  };

  // Handle saving selected avatar
  const handleSaveAvatar = async () => {
    // Validation
    if (!customAvatarName.trim()) {
      setNameError('Please enter a name for the orchestrator');
      return;
    }

    // Ensure we have both project ID and section ID to save orchestrator
    if (!projectId || !sectionId) {
      setNameError('Cannot save orchestrator without project and section context');
      return;
    }

    // Reset errors
    setNameError('');

    // Calculate these values outside the try block so they're available in the catch block too
    // Use selected avatar or create default avatar data if none selected
    const avatarPhotoUrl = selectedAvatar ? selectedAvatar.photoUrl : avatarData.url || 'defaultAvatarPath';
    const avatarType = selectedAvatar ? (selectedAvatar.type || 'robot') : 'robot';

    try {
      setSavingAvatar(true);

      // Debug log cache before save
      sectionAvatarService.listAllAvatarStorage();

      // Create data for avatar selection
      // Format exactly as expected by the API
      const newAvatarData = {
        sectionId, // Required for API path
        avatarName: customAvatarName.trim(),
        photoUrl: avatarPhotoUrl,
        type: avatarType,
        projectId, // Important: Include projectId for uniqueness
        // Additional fields required by the orchestrator API
        orchestratorName: customAvatarName.trim(),
        orchestratorRole: avatarType || 'robot'
      };

      // Use the avatar service to save project-specific avatar
      const response = await sectionAvatarService.saveSectionAvatar(sectionId, projectId, newAvatarData);

      // Always update UI regardless of success/failure
      // This ensures the UI remains consistent even if the server save failed
      // The service will handle the fallback to local storage if needed
      const updatedData = {
        url: response.avatarUrl || avatarPhotoUrl, // Use the variable defined outside the try/catch
        name: response.avatarName || customAvatarName,
        sectionId: sectionId
      };


      // Update local state regardless
      setAvatarData(updatedData);

      // Call callback if provided
      if (onAvatarChange) {
        onAvatarChange(updatedData.url, updatedData.name);
      }

      // Show updated storage state after save attempt
      sectionAvatarService.listAllAvatarStorage();

      // Show warning if save had issues but don't block the UI flow
      if (!response.success) {
        console.warn(`SectionOrchestrator: Issues with save operation:`, response);
        // Don't set error - just log it - to avoid blocking the UI flow
        // setNameError(response?.message || 'Failed to save orchestrator');
      }

      // Close dialog on success
      setAvatarDialogOpen(false);
    } catch (err) {
      console.error('Error updating section avatar:', err);

      // Since we're handling the error, update the UI with what we have anyway
      // This prevents the UI from getting stuck in a broken state
      const fallbackData = {
        url: avatarPhotoUrl, // Using the variable defined outside the try/catch
        name: customAvatarName,
        sectionId: sectionId
      };

      // Update state with fallback data
      setAvatarData(fallbackData);

      // Don't store in local cache
      console.log('Error saving avatar, but continuing with UI update');

      // Show error but don't block the UI flow
      console.warn(`Using fallback data for the UI:`, fallbackData);

      // Close dialog even on error
      setAvatarDialogOpen(false);

      // Optional: show a more user-friendly error message
      if (err.message && err.message.includes('already in use')) {
        // This is an expected error, show it as a name error
        setNameError(err.message);
      } else if (err.message && err.message.includes('timeout')) {
        // This is a timeout error, still close the dialog
        alert("Server took too long to respond. Orchestrator has been set locally but may not be saved to the server.");
      } else {
        // Generic error message but still close dialog
        alert("Could not save orchestrator to server. Changes have been applied locally.");
      }
    } finally {
      setSavingAvatar(false);
    }
  };

  return (
    <>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: 1,
        mb: 2,
        py: 0.5,
        position: 'relative'
      }}>
        {/* Orchestrator label - only show if there's an avatar name */}
        {(avatarData.name || customAvatarName) && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.8rem',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              opacity: 0.9,
              mb: 1
            }}
          >
            Orchestrator:
          </Typography>
        )}

        {/* Avatar and name on the next line */}
        {loading ? (
          <Box sx={{ ml: 0.5, display: 'flex', gap: 1, alignItems: 'center' }}>
            <CircularProgress size={16} thickness={4} />
            <Typography variant="body2">Loading orchestrator...</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              mt: 0.5,
              ml: 0.5
            }}
          >
            {/* Avatar with hover edit icon */}
            <Box
              sx={{
                position: 'relative',
                cursor: (projectId && sectionId) ? 'pointer' : 'default',
                '&:hover': (projectId && sectionId) ? {
                  opacity: 0.9,
                  transform: 'scale(1.05)',
                  transition: 'all 0.2s'
                } : {},
                '&:hover .edit-icon': {
                  opacity: 1
                },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                transition: 'all 0.2s'
              }}
              onClick={(projectId && sectionId) ? handleOpenAvatarDialog : undefined}
            >
              {avatarData.url ? (
                <Avatar
                  src={avatarData.url}
                  alt={avatarData.name}
                  onError={handleImageError}
                  sx={{
                    width: 48,
                    height: 48,
                    backgroundColor: sectionColorService.getSectionBackgroundColor(sectionId),
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                    }
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    fontSize: '1.2rem',
                    background: sectionColorService.getSectionBackgroundColor(sectionId),
                    color: sectionColorService.getSectionIconColor(sectionId),
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    '&:hover': {
                      boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                    }
                  }}
                >
                  {customAvatarName ? customAvatarName.charAt(0).toUpperCase() : ''}
                </Avatar>
              )}

              {/* Edit icon - controlled via permission */}
              {projectId && sectionId && (
                <PermissionGate permission="foundation.orchestrator.edit">
                  <Box
                    className="edit-icon"
                    sx={{
                      position: 'absolute',
                      right: -6,
                      bottom: -6,
                      opacity: 1,
                      transition: 'transform 0.2s ease, opacity 0.2s ease',
                      bgcolor: 'primary.main',
                      color: 'white',
                      borderRadius: '50%',
                      width: 22,
                      height: 22,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      zIndex: 1,
                      border: '1px solid white',
                      '&:hover': {
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <EditIcon sx={{ fontSize: '0.8rem' }} />
                  </Box>
                </PermissionGate>
              )}
            </Box>

            {/* Name displayed next to avatar */}
            {(avatarData.name || customAvatarName) && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.9rem',
                  color: '#333',
                  mt: 0.75
                }}
              >
                {avatarData.name || customAvatarName}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Avatar Selection Dialog */}
      <Dialog
        open={avatarDialogOpen}
        onClose={handleCloseAvatarDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '4px',
            overflow: 'visible',
            margin: 'auto',
            maxWidth: '90vw',
            width: '90vw',
            maxHeight: '90vh',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle component="div" sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          pl: 6,
          pr: 5,
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        }}>
          <Typography variant="h6" sx={{ fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
            {sectionName} Orchestrator
          </Typography>
          <IconButton onClick={handleCloseAvatarDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{
          pt: 4,
          overflow: 'visible', // Disable scrolling on the content container
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Name input with current avatar preview */}
          <Box sx={{
            mt: 3,
            mb: 3,
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: 'white',
            pt: 2,
            pb: 5.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: 2
            }}>
              {/* Avatar Preview */}
              <Box sx={{
                flex: 'none' // Prevent section from growing or shrinking
              }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                }}>
                  <Box>
                    {selectedAvatar?.photoUrl || avatarData.url ? (
                      <Box
                        component="img"
                        src={selectedAvatar?.photoUrl || avatarData.url}
                        alt="Selected avatar"
                        sx={{
                          width: 65,
                          height: 65,
                          borderRadius: '50%',
                          border: '3px solid #FFF5C0',
                          objectFit: 'contain',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          }
                        }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 65,
                          height: 65,
                          fontSize: 24,
                          bgcolor: sectionColorService.getSectionBackgroundColor(sectionId),
                          color: sectionColorService.getSectionIconColor(sectionId),
                          border: '3px solid #FFF5C0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          }
                        }}
                      >
                        {/* Always show first letter of current name */}
                        {customAvatarName ? customAvatarName.charAt(0).toUpperCase() : ''}
                      </Avatar>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Name Input Field */}
              <Box sx={{ flexGrow: 1, ml: 3 }}>
                <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                  Orchestrator Name
                </Typography>

                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={customAvatarName || ''} // Ensure it's never undefined
                  onChange={(e) => {
                    setCustomAvatarName(e.target.value);
                    if (nameError) setNameError('');
                  }}
                  placeholder="Enter orchestrator name" // Simple placeholder
                  autoFocus // Focus on the field when the dialog opens
                  InputProps={{
                    sx: {
                      borderRadius: '4px',
                      height: '45px'
                    }
                  }}
                  inputProps={{
                    'data-testid': 'orchestrator-name-input' // For testing purposes
                  }}
                  // Removed redundant "Orchestrator name" label since we already have it in the Typography above
                  error={!!nameError}
                  helperText={nameError || "Enter a name for this orchestrator"} // More descriptive help text
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#485869',
                        borderWidth: '1px'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#485869',
                        borderWidth: '1px'
                      }
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '16px',
                      '&::placeholder': {
                        fontSize: '15px',
                        opacity: 1,
                        color: 'rgba(0, 0, 0, 0.45)'  // Lighter color for placeholder
                      }
                    },
                    '& input::placeholder': {
                      fontSize: '15px',
                      opacity: 1,
                      color: 'rgba(0, 0, 0, 0.45)'  // Lighter color for placeholder
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Separator - removed since the name input section now has a bottom border */}

          {/* Orchestrator Selection */}
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, fontSize: '16px' }}>
            Select an orchestrator
          </Typography>

          {/* Avatar Grid Selection with improved styling */}
          <CustomAvatarSelector
            maxHeight= 'calc(100vh - 440px)'
            onSelectAvatar={handleSelectAvatar}
          />
        </DialogContent>

        {/* Dialog Actions */}
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 2 }}>
          <Button onClick={handleCloseAvatarDialog} color="inherit" variant='outlined'>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAvatar}
            variant="contained"
            color="primary"
            disabled={!customAvatarName.trim() || savingAvatar}
            startIcon={savingAvatar && <CircularProgress size={16} color="inherit" />}
          >
            {savingAvatar ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

SectionOrchestrator.propTypes = {
  sectionId: PropTypes.string,
  sectionName: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  avatarUrl: PropTypes.string,
  avatarName: PropTypes.string,
  onAvatarChange: PropTypes.func,
  consolidatedOrchestrator: PropTypes.object,
  useConsolidatedData: PropTypes.bool
};

export default SectionOrchestrator;